import * as React from 'react'
import { Placement, PlacementNS } from '../utils/types'

import { DEFAULT_RECT, Rect, getBoundingRect } from '../utils/dom'
import { getAnchorPosition } from '../utils/getAnchorPosition'
import { useBoundingRect } from './BoundingRect'
import { useEventListener } from '../effects/EventListener'

/* TYPES */

interface AnchorConfig {
  canFlip: boolean
  dependencies?: Array<any>
  padding: number
  placement: Placement
}

interface Props extends AnchorConfig {
  children: React.ReactNode
}

interface RenderProps extends AnchorConfig {
  children: (props: AnchorApi) => {}
}

export interface Anchor {
  ref: React.RefObject<HTMLElement>
  rect: Rect
}

interface AnchorApi {
  position: any
  overlay: Anchor
  target: Anchor
  portal: React.Ref<HTMLElement>
}

/* HOOK */

export const useAnchor: (config: AnchorConfig) => AnchorApi = ({
  canFlip = true,
  dependencies = [],
  placement = PlacementNS.bottomLeft,
  padding = 0,
}) => {
  const container = undefined

  const portal = React.createRef<HTMLElement>()

  const target = useBoundingRect({ dependencies })

  const overlay = useBoundingRect({ dependencies })

  const [position, setPosition] = React.useState({ top: 0, left: 0, placement })

  const getPosition = () =>
    getAnchorPosition(
      placement,
      padding,
      container,
      getBoundingRect(target.ref.current),
      getBoundingRect(overlay.ref.current),
      canFlip
    )

  const updatePortal = (style: {
    left: number
    top: number
    minWidth: number
  }) => {
    if (overlay.ref.current && portal.current) {
      const left = Math.floor(style.left)
      const top = Math.floor(style.top)
      portal.current.style.transform = `translate(${left}px, ${top}px)`
      portal.current.style.minWidth = `${style.minWidth}px`
    }
  }

  const viewChangeHandler = () => {
    const pos = getPosition()
    updatePortal(pos)
    pos.placement !== position.placement && setPosition(pos)
    // debounce(100, setPosition(position))
  }

  useEventListener({
    event: 'scroll',
    options: { capture: true },
    handler: viewChangeHandler,
  })

  useEventListener({
    event: 'resize',
    handler: viewChangeHandler,
  })

  React.useEffect(() => {
    const pos = getPosition()
    setPosition(pos)
    updatePortal(pos)
  }, dependencies)

  return {
    portal,
    position,
    target,
    overlay,
  }
}

/* DECORATOR */

export const withAnchor = <T extends {}>(config: AnchorConfig) => (
  Component: React.ComponentType<T>
) => (props: T & { anchor: AnchorConfig }) => (
  <Component {...props} anchor={useAnchor({ ...config, ...props.anchor })} />
)

/* RENDER PROP */

export const Anchor = ({ children, ...props }: RenderProps) => {
  const anchor = useAnchor(props)

  return typeof children === 'function' ? children(anchor) : children
}

/* CONTEXT */

const context: AnchorApi = {
  portal: React.createRef(),
  position: null,
  target: {
    ref: React.createRef(),
    rect: DEFAULT_RECT,
  },
  overlay: {
    ref: React.createRef(),
    rect: DEFAULT_RECT,
  },
}

export const AnchorContext = React.createContext(context)

export const AnchorProvider = ({ children, ...props }: Props) => (
  <AnchorContext.Provider value={useAnchor(props)}>
    {children}
  </AnchorContext.Provider>
)

export const consumeAnchor = () => React.useContext(AnchorContext)
